import React, { useState, useEffect, useRef, useCallback } from 'react';
import './App.css';

function App() {
  const [language, setLanguage] = useState('en');
  const videoRef = useRef(null);

  // متون چندزبانه
  const texts = {
    en: {
      logo: "Kabora Quest",
      headline: "Something amazing is coming...",
      days: "Days",
      hours: "Hours",
      minutes: "Minutes",
      seconds: "Seconds",
      placeholder: "Your email",
      button: "Notify Me",
      rtl: false,
      toast: "Email entered successfully!"
    },
    fa: {
      logo: "کابورا کویست",
      headline: "چیزی شگفت‌انگیز در راه است...",
      days: "روز",
      hours: "ساعت",
      minutes: "دقیقه",
      seconds: "ثانیه",
      placeholder: "ایمیل شما",
      button: "خبرم کن",
      rtl: true,
      toast: "ایمیل شما ثبت شد!"
    },
    ps: {
      logo: "کابورا کوېسټ",
      headline: "یو څه حیرانوونکی راروان دی...",
      days: "ورځې",
      hours: "ساعتونه",
      minutes: "دقیقې",
      seconds: "ثانیې",
      placeholder: "ستاسې ایمیل",
      button: "خبر راکړه",
      rtl: true,
      toast: "ستاسې ایمیل ثبت شو!"
    }
  };

  // تاریخ countdown از localStorage یا ایجاد جدید
  const getCountdownDate = () => {
    const stored = localStorage.getItem('kaboraCountdown');
    if (stored) {
      return new Date(stored);
    }
    const newDate = new Date();
    newDate.setDate(newDate.getDate() + 30);
    localStorage.setItem('kaboraCountdown', newDate);
    return newDate;
  };

  const countdownDate = getCountdownDate();
  const [timeLeft, setTimeLeft] = useState({});
  const [showToast, setShowToast] = useState(false);
  const [email, setEmail] = useState('');

  // محاسبه زمان باقی‌مانده با useCallback
  const calculateTimeLeft = useCallback(() => {
    const now = Date.now();
    const distance = countdownDate - now;

    if (distance <= 0) {
      return { days: '00', hours: '00', minutes: '00', seconds: '00' };
    }

    return {
      days: String(Math.floor(distance / 86400000)).padStart(2, '0'),
      hours: String(Math.floor(distance % 86400000 / 3600000)).padStart(2, '0'),
      minutes: String(Math.floor(distance % 3600000 / 60000)).padStart(2, '0'),
      seconds: String(Math.floor(distance % 60000 / 1000)).padStart(2, '0')
    };
  }, [countdownDate]);

  // تایمر
  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft());
    }, 1000);

    // مقدار اولیه
    setTimeLeft(calculateTimeLeft());

    return () => clearInterval(timer);
  }, [calculateTimeLeft]);

  // مدیریت تغییر زبان
  const handleLanguageChange = (e) => {
    setLanguage(e.target.value);
  };

  // مدیریت ثبت ایمیل
  const handleSubmit = () => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (emailRegex.test(email)) {
      setShowToast(true);
      setTimeout(() => setShowToast(false), 2000);
      setEmail('');
    } else {
      alert('Invalid email');
    }
  };

  // مدیریت کلید Enter
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSubmit();
    }
  };

  const currentText = texts[language];

  return (
    <div className={`App ${currentText.rtl ? 'rtl' : ''}`}>
      {/* Toast */}
      <div id="toast" style={{
        position: 'fixed',
        top: '20px',
        right: '20px',
        background: '#28a745',
        color: 'white',
        padding: '15px 20px',
        borderRadius: '8px',
        opacity: showToast ? 1 : 0,
        pointerEvents: 'none',
        transition: 'opacity 0.5s, transform 0.5s',
        transform: showToast ? 'translateY(0)' : 'translateY(-20px)',
        zIndex: 100
      }}>
        {currentText.toast}
      </div>

      {/* Background Video */}
      <video
        ref={videoRef}
        autoPlay
        muted
        loop
        id="bgVideo"
        style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          width: '110%',
          height: '110%',
          objectFit: 'cover',
          transform: 'translate(-50%, -50%)',
          zIndex: -1,
          filter: 'brightness(0.5) blur(5px)'
        }}
      >
        <source src="bg.mp4" type="video/mp4" />
      </video>

      {/* Language Selector */}
      <select
        id="languageSelect"
        className="language-select"
        value={language}
        onChange={handleLanguageChange}
        style={{
          position: 'absolute',
          top: '20px',
          left: '20px',
          padding: '5px 10px',
          borderRadius: '5px',
          border: 'none',
          background: 'rgba(0,0,0,0.5)',
          color: 'white',
          cursor: 'pointer'
        }}
      >
        <option value="en">English</option>
        <option value="fa">فارسی</option>
        <option value="ps">پشتو</option>
      </select>

      {/* Logo */}
      <img
        src="logo.svg"
        alt="Kabora Quest Logo"
        className="logo"
        style={{
          width: '100px',
          marginBottom: '30px',
          filter: 'drop-shadow(0 4px 40px rgba(255,255,255,0.9))'
        }}
      />

      {/* Title */}
      <h1 id="logoText" style={{ fontSize: '3em', marginBottom: '0.5em' }}>
        {currentText.logo}
      </h1>
      <p id="headline" style={{ fontSize: '1.3em', marginBottom: '1em' }}>
        {currentText.headline}
      </p>

      {/* Countdown */}
      <div className="countdown" style={{
        display: 'flex',
        justifyContent: 'center',
        gap: '15px',
        marginBottom: '20px'
      }}>
        {['days', 'hours', 'minutes', 'seconds'].map((unit) => (
          <div key={unit} style={{
            background: 'rgba(255,255,255,0.1)',
            backdropFilter: 'blur(30px)',
            width: '90px',
            height: '90px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            borderRadius: '10px',
            fontSize: '1.3em'
          }}>
            <span id={unit}>{timeLeft[unit] || '00'}</span>
            <small id={`${unit}Label`} style={{
              fontSize: '0.7em',
              lineHeight: 1,
              paddingTop: '2px'
            }}>
              {currentText[unit]}
            </small>
          </div>
        ))}
      </div>

      {/* Email Form */}
      <div>
        <input
          type="email"
          id="emailInput"
          placeholder={currentText.placeholder}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          onKeyPress={handleKeyPress}
          style={{
            padding: '10px',
            width: '250px',
            border: 'none',
            borderRadius: '5px'
          }}
        />
        <button
          id="notifyBtn"
          onClick={handleSubmit}
          style={{
            padding: '10px 20px',
            border: 'none',
            borderRadius: '5px',
            background: '#eb0028',
            color: 'white',
            cursor: 'pointer',
            marginLeft: '10px',
            marginRight: '10px'
          }}
          onMouseEnter={(e) => e.target.style.background = '#100E09'}
          onMouseLeave={(e) => e.target.style.background = '#eb0028'}
        >
          {currentText.button}
        </button>
      </div>

      {/* Footer */}
      <footer style={{
        position: 'absolute',
        bottom: '10px',
        width: '100%',
        textAlign: 'center',
        fontSize: '0.9em',
        color: 'rgba(255,255,255,0.7)'
      }}>
        &copy; {new Date().getFullYear()} <span id="logoFooter">{currentText.logo}</span>
      </footer>
    </div>
  );
}

export default App;